import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { getAuthData } from "../../utils/auth";

const apiUrl = process.env.REACT_APP_API_URL;

const OPCIONES_CUOTAS = [
  { label: 3, key: "cuota_1" },
  { label: 6, key: "cuota_2" },
  { label: 9, key: "cuota_3" },
  { label: 12, key: "cuota_4" },
  { label: 18, key: "cuota_5" },
];

const Columbarios = ({ agregarAlPresupuesto, resetTrigger, deshabilitarBoton }) => {
  const [codigo, setCodigo] = useState("");
  const [columbario, setColumbario] = useState({});
  const [modalidad_pago, setModalidadPago] = useState("");
  const [cuotas, setCuotas] = useState(3);
  const [monto_por_cuota, setMontoCuotas] = useState(0);
  const [precio_plan, setPrecioPlan] = useState(0);
  const [planSeleccionado, setPlanSeleccionado] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [metodo_pago, setMetodoPago] = useState("");
  const [contadoChecked, setContadoChecked] = useState(false);
  const [imagenes, setImagenes] = useState([]);
  const [capillaSeleccionada, setCapillaSeleccionada] = useState("CP1");

  const opcionesCapilla = [
    { value: "CP1", label: "Capilla Principal" },
    { value: "CC1", label: "Capilla de Contemplación 1" },
    { value: "CC2", label: "Capilla de Contemplación 2" },
    { value: "CC3", label: "Capilla de Contemplación 3" },
    { value: "CC4", label: "Capilla de Contemplación 4" },
  ];

  const auth = getAuthData();
  const token = auth?.token;

  const columbarios = [
    {
      nombre: "Columbario Izquierdo - Externo",
      archivo: "Lado_A.jpg"
    },
    {
      nombre: "Columbario Izquierdo - Interno",
      archivo: "Lado_B.jpg"
    },
    {
      nombre: "Columbario Derecho - Interno",
      archivo: "Lado_C.jpg"
    },
    {
      nombre: "Columbario Derecho - Externo",
      archivo: "Lado_D.jpg"
    },
  ];

  useEffect(() => {

    let objectUrls = [];

    const fetchImagenes = async () => {
      try {

        const resultados = await Promise.all(
          columbarios.map(async (item) => {
            const response = await axios.get(
              `${apiUrl}/api/columbarios/mapa/${capillaSeleccionada}/${item.archivo}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`
                },
                responseType: "blob"
              }
            );

            const url = URL.createObjectURL(response.data);
            objectUrls.push(url);

            return {
              nombre: item.nombre,
              url: url
            };

          })
        );

        setImagenes(resultados);

      } catch (error) {

        console.error("Error cargando columbarios:", error);
        toast.error("No se pudieron cargar los mapas.");

      }
    };

    if (token && capillaSeleccionada) {
      fetchImagenes();
    }

    return () => {
      objectUrls.forEach(url => URL.revokeObjectURL(url));
    };

  }, [token, apiUrl, capillaSeleccionada]);

  const abrirImagen = (url) => {
    if (!url) {
      toast.error("Imagen no disponible.");
      return;
    }
    window.open(url, "_blank");
  };

  const precioContado_bcv = planSeleccionado
    ? planSeleccionado.de_contado_bcv || 0
    : 0;
  const precioContado = planSeleccionado ? planSeleccionado.de_contado || 0 : 0;

  // Calcular cuotas disponibles
  const availableQuotas = React.useMemo(() => {
    if (!columbario.id) return [];
    return OPCIONES_CUOTAS.filter((option) => {
      const val = columbario[option.key];
      return val !== null && val !== undefined && parseFloat(val) > 0;
    });
  }, [columbario]);

  // Effect to ensure valid quota selection
  useEffect(() => {
    if (modalidad_pago === "financiado" && availableQuotas.length > 0) {
      const isCurrentValid = availableQuotas.some((q) => q.label === cuotas);
      if (!isCurrentValid) {
        setCuotas(availableQuotas[0].label);
      }
    }
  }, [modalidad_pago, availableQuotas, cuotas]);

  const handleCodigoChange = (e) => {
    setCodigo(e.target.value);
  };

  const handleCodigo = async () => {
    if (!codigo) {
      toast.error("Ingrese un código de columbario");
      return;
    }
    try {
      const response = await axios.get(`${apiUrl}/api/consulta-columbario/${codigo}`);
      const plan = response.data;
      setColumbario(plan);
      setPlanSeleccionado(plan);
      toast.success("Columbario disponible y precios cargados");
    } catch (error) {
      setColumbario({});
      const mensaje = error.response?.data?.detail || "Error al consultar código";
      toast.error(mensaje);
    }
  };

  useEffect(() => {
    if (!columbario.id) return;

    if (modalidad_pago === "contado") {
      const precio = contadoChecked ? precioContado : precioContado_bcv;
      const metodo = contadoChecked ? "USD" : "VES";

      setPrecioPlan(precio);
      setMontoCuotas(0);
      setMetodoPago(metodo);

    } else if (modalidad_pago === "financiado") {
      const opcion = OPCIONES_CUOTAS.find((o) => o.label === cuotas);

      const precioTotalFinanciado = opcion
        ? (parseFloat(columbario[opcion.key]) || 0)
        : 0;

      const cuotaMensual = cuotas > 0
        ? precioTotalFinanciado / cuotas
        : 0;

      setMontoCuotas(cuotaMensual);
      setPrecioPlan(precioTotalFinanciado);

      // 🔥 FINANCIADO SIEMPRE USD
      setMetodoPago("USD");
    }
  }, [modalidad_pago, cuotas, columbario, contadoChecked, precioContado, precioContado_bcv]);

  const handleAgregar = () => {
    if (!modalidad_pago || !columbario.id) {
      toast.error("Por favor, seleccione una modalidad de pago y consulte un código válido.");
      return;
    }

    const item = {
      tipo_servicio: "nichos-columbarios",
      tipo_plan: "producto",
      descripcion: columbario.descripcion,
      modalidad_pago: modalidad_pago,
      codigo,
      metodo_pago: metodo_pago,
      modalidad_pago: modalidad_pago,
      pago_inicial: 0,
      numero_cuotas:
        modalidad_pago === "financiado" ? cuotas : null,
      monto_por_cuota:
        modalidad_pago === "financiado" ? monto_por_cuota : null,
      precio_plan: precio_plan,

      detalles_adicionales: {
        codigo,
        capilla: columbario.capilla,
        lado: columbario.lado,
        cantidad_nichos: columbario.cantidad_nichos,
        fila: columbario.fila,
        columna: columbario.columna,
        estado: "reservado",
      },
    };

    agregarAlPresupuesto(item);
  };

  useEffect(() => {
    if (resetTrigger) {
      resetFormulario();
    }
  }, [resetTrigger]);

  const resetFormulario = () => {
    setCodigo("");
    setColumbario({});
    setPlanSeleccionado(null);
    setModalidadPago("");
    setCuotas(3);
    setMontoCuotas(0);
    setContadoChecked(false);
  };


  return (
    <>
      <Typography
        sx={{
          marginBottom: "5px",
          fontFamily: "Bakbak One, sans-serif",
          color: "#0A4575",
          textAlign: "center",
        }}
        variant="h5"
      >
        SOLICITUD DE CONTRATO NICHOS DE COLUMBARIOS
      </Typography>
      <Box>
        <Typography
          sx={{
            marginTop: "15px",
            marginBottom: "10px",
            fontFamily: "Bakbak One, sans-serif",
            color: "#0A4575",
          }}
          variant="h6"
        >
          PLAN A SOLICITAR
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={2}>
          <TextField
            fullWidth
            label="Código"
            variant="outlined"
            size="small"
            name="codigo_columbario"
            value={codigo || ""}
            onChange={handleCodigoChange}
            onBlur={handleCodigo}
            sx={{ boxShadow: 3 }}
          />
        </Grid>
        <Grid item>
          <IconButton onClick={() => setIsOpen(true)}>
            <HelpOutlineIcon />
          </IconButton>
        </Grid>
        <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontFamily: "Bakbak One, sans-serif", color: "#0A4575", textAlign: "center" }}>
            Códigos Nichos de Columbarios
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, mt: 1 }}>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 200, boxShadow: 3 }}>
                <InputLabel>Seleccione Capilla</InputLabel>
                <Select
                  value={capillaSeleccionada}
                  onChange={(e) => setCapillaSeleccionada(e.target.value)}
                  label="Seleccione Capilla"
                >
                  {opcionesCapilla.map(opc => (
                    <MenuItem key={opc.value} value={opc.value}>{opc.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Grid container spacing={2} justifyContent="center">
              {imagenes.map((item, index) => (
                <Grid item key={index}>
                  <Button
                    variant="outlined"
                    onClick={() => abrirImagen(item.url)}
                  >
                    {item.nombre}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
        </Dialog>

        {columbario.id && (
          <>
            <Grid item xs={1}>
              <TextField
                fullWidth
                label="Capilla"
                variant="outlined"
                size="small"
                value={columbario.capilla || ""}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ boxShadow: 3 }}
              />
            </Grid>
            <Grid item xs={1}>
              <TextField
                fullWidth
                label="Lado"
                variant="outlined"
                size="small"
                value={columbario.lado || ""}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ boxShadow: 3 }}
              />
            </Grid>
            <Grid item xs={1}>
              <TextField
                fullWidth
                label="Nichos"
                variant="outlined"
                size="small"
                value={columbario.cantidad_nichos || ""}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ boxShadow: 3 }}
              />
            </Grid>
            <Grid item xs={1}>
              <TextField
                fullWidth
                label="Fila"
                variant="outlined"
                size="small"
                value={columbario.fila || ""}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ boxShadow: 3 }}
              />
            </Grid>
            <Grid item xs={1}>
              <TextField
                fullWidth
                label="Columna"
                variant="outlined"
                size="small"
                value={columbario.columna || ""}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ boxShadow: 3 }}
              />
            </Grid>
          </>
        )}
      </Grid>

      {columbario.id && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="Plan"
              variant="outlined"
              size="small"
              value={columbario.descripcion || ""}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={{ boxShadow: 3 }}
            />
          </Grid>

          <Grid item xs={2}>
            <FormControl fullWidth variant="outlined" size="small" sx={{ boxShadow: 3 }}>
              <InputLabel>Modalidad de Pago</InputLabel>
              <Select
                value={modalidad_pago}
                onChange={(e) => {
                  const val = e.target.value;
                  setModalidadPago(val);

                  if (val === "financiado") {
                    setContadoChecked(true); // siempre USD
                  }
                }}
                label="Modalidad de Pago"
              >
                <MenuItem value="" disabled>Seleccione una opción</MenuItem>
                <MenuItem value="contado">De Contado</MenuItem>
                <MenuItem value="financiado">Financiado</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {modalidad_pago === "contado" && (
            <>
              <Grid item xs={2}>
                <TextField
                  fullWidth
                  label="Monto Contado"
                  variant="outlined"
                  size="small"
                  value={`$${precio_plan}`}
                  InputProps={{ readOnly: true }}
                  sx={{ boxShadow: 3 }}
                />
              </Grid>
              <Grid item xs={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={modalidad_pago === "financiado" ? true : contadoChecked}
                      onChange={(e) => setContadoChecked(e.target.checked)}
                      disabled={modalidad_pago === "financiado"}
                      color="primary"
                    />
                  }
                  label="Pago en Divisas"
                />
              </Grid>
              <Grid item xs={3}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAgregar}
                  disabled={!modalidad_pago || deshabilitarBoton}
                  sx={{ backgroundColor: "#0A4575", "&:hover": { backgroundColor: "#083a5e" } }}
                >
                  Crear Solicitud
                </Button>
              </Grid>
            </>
          )}

          {modalidad_pago === "financiado" && (
            <>
              <Grid item xs={2}>
                <TextField
                  fullWidth
                  label="Total Financiado"
                  variant="outlined"
                  size="small"
                  value={precio_plan}
                  InputProps={{ readOnly: true }}
                  sx={{ boxShadow: 3 }}
                />
              </Grid>
              <Grid item xs={1}>
                <FormControl fullWidth size="small" sx={{ boxShadow: 3 }}>
                  <InputLabel>Cuotas</InputLabel>
                  <Select value={cuotas} label="Cuotas" onChange={(e) => setCuotas(e.target.value)}>
                    {OPCIONES_CUOTAS.map((opcion) =>
                      parseFloat(columbario[opcion.key]) > 0 ? (
                        <MenuItem key={opcion.key} value={opcion.label}>{opcion.label}</MenuItem>
                      ) : null
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={2}>
                <TextField
                  fullWidth
                  label="Monto por Cuota"
                  variant="outlined"
                  size="small"
                  value={monto_por_cuota.toFixed(2)}
                  InputProps={{ readOnly: true }}
                  sx={{ boxShadow: 3 }}
                />
              </Grid>
              <Grid item xs={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAgregar}
                  disabled={!modalidad_pago || deshabilitarBoton}
                  sx={{ backgroundColor: "#0A4575", "&:hover": { backgroundColor: "#083a5e" } }}
                >
                  Crear Solicitud
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      )}
    </>
  );
};

export default Columbarios;